import {
  autoUpdate,
  flip,
  FloatingPortal,
  hide,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { startOfMonth } from 'date-fns'
import { ChangeEvent, FC, useState } from 'react'

import { IDate } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDateRange } from '@avantstay/format-date-range'

import { HorizontalAlignment } from '../../enums'
import {
  formatDate,
  formatDateRangeExtraOptions,
  getDatesFromPeriod,
  getPeriodFromDates,
  parseISODate,
  totalNights,
} from '../../utils'
import { Loading } from '../Loading'
import { TextField } from '../TextField'
import { AvailabilityCalendar, DateRangeValue } from './AvailabilityCalendar'
import { CalendarDay, HighlightedRange } from './types'
import * as S from './AvailabilityDateRangePicker.styles'

export interface AvailabilityDateRangePickerProps {
  // Omit to allow every future date; supply it to constrain selection to real availability.
  days?: CalendarDay[]
  // Narrows selection to a window without needing a day for every date.
  minDate?: string
  maxDate?: string
  // Pick one day rather than a stay: dateValue and onChange carry a single ISO date.
  singleDate?: boolean
  singleDateFormat?: string
  // Opt in to 'any future date' for forms with no availability to honour. Omit it and an empty
  // `days` stays closed, which is what a failed availability fetch must look like.
  allowAnyFutureDate?: boolean
  dateValue?: string
  onChange: (period: string) => void
  disabled?: boolean
  placeholder?: string
  showNumberOfNights?: boolean
  horizontalAlignment?: HorizontalAlignment
  totalAmount?: string
  originalRange?: { startDate: IDate; endDate: IDate }
  highlightedRange?: HighlightedRange
  error?: string
  clearLabel?: string
  inputDataCy?: string
  loading?: boolean
}

const formatRange = (fromDate?: string, toDate?: string): string => {
  if (!fromDate || !toDate) return ''
  return formatDateRange(
    `${formatDate(fromDate)}T00:00:00`,
    `${formatDate(toDate)}T00:00:00`,
    'en-US',
    formatDateRangeExtraOptions,
  )
}

export const AvailabilityDateRangePicker: FC<AvailabilityDateRangePickerProps> = ({
  days = [],
  minDate,
  maxDate,
  singleDate = false,
  singleDateFormat = 'MM/dd/yyyy',
  allowAnyFutureDate = false,
  dateValue,
  onChange,
  disabled = false,
  placeholder,
  showNumberOfNights = false,
  horizontalAlignment = HorizontalAlignment.Left,
  totalAmount,
  originalRange,
  highlightedRange,
  error,
  clearLabel = 'Clear',
  inputDataCy,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  // Portal + fixed positioning keeps the two-month popover out of the modify side panel's overflow.
  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: horizontalAlignment === HorizontalAlignment.Right ? 'bottom-end' : 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({ padding: 8 }), hide()],
  })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  // An empty value must stay empty: getDatesFromPeriod('') resolves to today on both ends, which
  // rendered a phantom same-day range in a field the agent had not filled in yet.
  const single = singleDate ? dateValue || undefined : undefined
  const { fromDate, toDate } = singleDate
    ? { fromDate: single, toDate: single }
    : (dateValue ? getDatesFromPeriod(dateValue) : { fromDate: undefined, toDate: undefined })
  const nights =
    fromDate && toDate && fromDate !== toDate ? totalNights(fromDate, toDate) : undefined
  const rangeLabel = singleDate
    ? (single ? formatDate(`${single}T00:00:00`, singleDateFormat) : '')
    : formatRange(fromDate, toDate)
  const inputValue =
    showNumberOfNights && rangeLabel && nights ? `${rangeLabel} • ${nights}` : rangeLabel

  // Only strike through the original once the selection actually differs from it.
  const originalStart = originalRange ? formatDate(originalRange.startDate) : undefined
  const originalEnd = originalRange ? formatDate(originalRange.endDate) : undefined
  const hasChangedFromOriginal =
    !!originalStart && (originalStart !== fromDate || originalEnd !== toDate)

  const handleCalendarChange = (range: DateRangeValue) => {
    if (singleDate) {
      if (range.checkInDate) {
        onChange(range.checkInDate)
        setIsOpen(false)
      }
      return
    }

    if (range.checkInDate && range.checkOutDate) {
      onChange(getPeriodFromDates({ fromDate: range.checkInDate, toDate: range.checkOutDate }))
      setIsOpen(false)
    }
  }

  const handleClear = () => {
    onChange('')
    setIsOpen(false)
  }

  return (
    <S.Root ref={refs.setReference} {...getReferenceProps()}>
      <TextField.Default
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        dataCy={inputDataCy}
        inputRightIcon={loading ? <Loading size="4px" /> : <S.IconCalendarStyled />}
        inputLeftIcon={
          hasChangedFromOriginal ? (
            <S.OriginalRange>{formatRange(originalStart, originalEnd)}</S.OriginalRange>
          ) : null
        }
        onClick={() => {
          if (!disabled) setIsOpen(true)
        }}
        onFocus={event => {
          event.persist()
          event.target.blur()
        }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.value)}
      />

      {isOpen && !disabled ? (
        <FloatingPortal>
          <S.Popover
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
            }}
            {...getFloatingProps()}
          >
            <S.CalendarContainer>
              <AvailabilityCalendar
                days={days}
                minDate={minDate}
                maxDate={maxDate}
                singleDate={singleDate}
                allowAnyFutureDate={allowAnyFutureDate}
                highlightedRange={highlightedRange}
                defaultValue={{
                  checkInDate: fromDate || undefined,
                  checkOutDate: toDate || undefined,
                }}
                initialMonth={fromDate ? startOfMonth(parseISODate(fromDate)) : undefined}
                onChange={handleCalendarChange}
              />
              {loading ? (
                <S.LoadingOverlay>
                  <Loading />
                </S.LoadingOverlay>
              ) : null}
            </S.CalendarContainer>
            <S.Footer>
              <S.Summary>
                {rangeLabel ? <span>{rangeLabel}</span> : null}
                {nights ? <span>{`• ${nights}`}</span> : null}
                {totalAmount ? <span>{`• ${totalAmount}`}</span> : null}
              </S.Summary>
              <S.ClearButton type="button" onClick={handleClear}>
                {clearLabel}
              </S.ClearButton>
            </S.Footer>
          </S.Popover>
        </FloatingPortal>
      ) : null}
    </S.Root>
  )
}
